/**
 * Saves an item to local storage with the specified key.
 *
 * @param {string} key - The key under which to store the data.
 * @param {*} data - The data to be stored.
 * @param {boolean} [addTimestamp=true] - If true, will add a timestamp to the stored data.
 * @returns {void}
 */
const saveStorage = function (key: string, data: any, addTimestamp: boolean = true): void {
    if (addTimestamp) {
        const storageTimestamp = new Date().getTime();
        localStorage.setItem(key, JSON.stringify({ ...data, storageTimestamp }));
        return;
    }
    localStorage.setItem(key, JSON.stringify(data));
};

/**
 * Looks for a local storage item and returns if present.
 *
 * @param {string} key - The key of the item to retrieve.
 * @param {string} [item] - Optional specific item to retrieve from the stored object.
 * @returns {*} The retrieved item or null if not found.
 */
const getStorage = function (key: string, item?: string): any {
    if (localStorage.getItem(key) && item) {
        const data = JSON.parse(localStorage.getItem(key) || "{}");
        return data[item];
    } else if (localStorage.getItem(key)) {
        return JSON.parse(localStorage.getItem(key) || "{}");
    }
    return null;
};

/**
 * Clears a single item or the entire local storage.
 *
 * @param {string} [key=null] - The key of the item to remove. If not provided, clears all items.
 * @returns {void}
 */
const clearStorage = function (key: string | null = null): void {
    if (key) {
        localStorage.removeItem(key);
    } else {
        localStorage.clear();
    }
};

/**
 * Module for managing local storage operations.
 *
 * @module UseStorage
 * @exports {Function} getStorage - Retrieves data from local storage.
 * @exports {Function} saveStorage - Saves data to local storage.
 * @exports {Function} clearStorage - Clears data from local storage.
 */
export { getStorage, saveStorage, clearStorage };
